import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateCase() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    pet_name: '',
    pet_species: '',
    pet_breed: '',
    pet_age: '',
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    reason: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/clinic/cases`,
        {
          ...formData,
          pet_age: formData.pet_age ? parseInt(formData.pet_age) : null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('¡Caso creado exitosamente!');
      navigate(`/clinic/cases/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el caso');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/clinic/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al Dashboard
        </Button>

        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Crear Nuevo Caso</h1>
          <p className="text-slate-600 mb-8">Registra una nueva mascota en la clínica</p>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Pet Information */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Información de la Mascota</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre de la Mascota *
                  </label>
                  <Input
                    type="text"
                    value={formData.pet_name}
                    onChange={(e) => setFormData({...formData, pet_name: e.target.value})}
                    placeholder="Max"
                    required
                    data-testid="pet-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Especie *
                  </label>
                  <Select
                    value={formData.pet_species}
                    onValueChange={(value) => setFormData({...formData, pet_species: value})}
                    required
                  >
                    <SelectTrigger data-testid="pet-species-select">
                      <SelectValue placeholder="Seleccionar especie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Perro">Perro</SelectItem>
                      <SelectItem value="Gato">Gato</SelectItem>
                      <SelectItem value="Ave">Ave</SelectItem>
                      <SelectItem value="Conejo">Conejo</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Raza
                  </label>
                  <Input
                    type="text"
                    value={formData.pet_breed}
                    onChange={(e) => setFormData({...formData, pet_breed: e.target.value})}
                    placeholder="Labrador"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Edad (años)
                  </label>
                  <Input
                    type="number"
                    value={formData.pet_age}
                    onChange={(e) => setFormData({...formData, pet_age: e.target.value})}
                    placeholder="3"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Owner Information */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Información del Dueño</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre Completo *
                  </label>
                  <Input
                    type="text"
                    value={formData.owner_name}
                    onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                    placeholder="Juan Pérez"
                    required
                    data-testid="owner-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Teléfono *
                  </label>
                  <Input
                    type="tel"
                    value={formData.owner_phone}
                    onChange={(e) => setFormData({...formData, owner_phone: e.target.value})}
                    placeholder="5512345678"
                    required
                    data-testid="owner-phone-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.owner_email}
                    onChange={(e) => setFormData({...formData, owner_email: e.target.value})}
                    placeholder="juan@example.com"
                  />
                </div>
              </div>
            </div>

            {/* Case Details */}
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-4">Detalles del Caso</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Razón de Ingreso *
                  </label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                    placeholder="Chequeo general, cirugía, emergencia..."
                    required
                    rows={4}
                    data-testid="reason-input"
                  />
                  <p className="text-sm text-slate-500 mt-1">
                    Después de crear el caso, podrás generar un código NFC para compartir con el dueño
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="submit-case-button"
              >
                {loading ? 'Creando...' : 'Crear Caso'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/clinic/dashboard')}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
